/* eslint-disable react-hooks/exhaustive-deps */
import {
  Avatar,
  Box,
  Button,
  capitalize,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
import { ILabel, TLabelCategory } from 'src/models/general';
import SearchIcon from '@mui/icons-material/Search';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CloseIcon from '@mui/icons-material/Close';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import ModalNewLabel from '../ModalNewLabel';
import { useTranslation } from 'react-i18next';

interface ModalSelectLabelProps {
  onClose: (event: {}, reason?: 'backdropClick' | 'escapeKeyDown') => void;
  open: boolean;
  prevSelectedLabels?: Array<ILabel>;
  onSubmit: (labelsSelected: Array<ILabel>) => void;
  title?: string;
  labelList: Array<ILabel>;
  hasLabelCategory?: boolean;
}

const TableCellHeader = styled(TableCell)(
  ({ theme }) => `
        z-index: 3;
        background: ${theme.palette.primary.main};
        color: ${theme.palette.common.white};
    `
);

const ModalSelectLabel: React.FC<ModalSelectLabelProps> = ({
  open,
  onClose,
  onSubmit,
  prevSelectedLabels = [],
  title = 'Select Label',
  labelList,
  hasLabelCategory = false
}) => {
  const { t } = useTranslation();
  const [selectedLabelsLocal, setSelectedLabelsLocal] =
    useState<Array<ILabel>>(prevSelectedLabels);
  const theme = useTheme();
  const [selectedLabelCategory, setSelectedLabelCategory] =
    useState<TLabelCategory | null>('all');

  const isSelected = (id: string) =>
    selectedLabelsLocal.findIndex((obj) => obj.id === id) !== -1;

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Add items that aren't already selected
    if (event.target.checked) {
      const existingSelectedMap = new Map(
        selectedLabelsLocal.map((item) => [item.id, item])
      );

      // Merge existing selections with new items in one operation
      setSelectedLabelsLocal((prevSelected) => {
        // If nothing is currently selected, just use the entire list
        if (prevSelected.length === 0) {
          return [...infiniteScroll.list];
        }

        const result = [...prevSelected];
        for (const item of infiniteScroll.list) {
          if (!existingSelectedMap.has(item.id)) {
            result.push(item);
          }
        }
        return result;
      });
      return;
    }
    // Remove items that are already selected in visible list
    const idsToRemove = new Set(infiniteScroll.list.map((item) => item.id));
    setSelectedLabelsLocal((prev) =>
      prev.filter((item) => !idsToRemove.has(item.id))
    );
  };

  const handleClickLabel = function (label: ILabel) {
    const selectedIndex = selectedLabelsLocal.findIndex(
      (obj) => obj.id === label.id
    );
    let newSelected: ILabel[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedLabelsLocal, label);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedLabelsLocal.slice(1));
    } else if (selectedIndex === selectedLabelsLocal.length - 1) {
      newSelected = newSelected.concat(selectedLabelsLocal.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedLabelsLocal.slice(0, selectedIndex),
        selectedLabelsLocal.slice(selectedIndex + 1)
      );
    }
    setSelectedLabelsLocal(newSelected);
  };

  const { start, end } = useAppSelector(
    (state) => state.analyticArguments.dateRangeFormatted
  );
  const [selectTimeFrame, setSelectTimeFrame] = useState<boolean>(false);
  const filterByDate = useMemo((): Array<ILabel> => {
    const filteredLabelList = hasLabelCategory
      ? labelList.filter((item) =>
          selectedLabelCategory !== 'all'
            ? item.labelCategory === selectedLabelCategory
            : item
        )
      : labelList;

    const filteredLabelWithDisplayName = filteredLabelList.map((label) => ({
      ...label,
      name: label.displayName
    }));

    if (!selectTimeFrame) return filteredLabelWithDisplayName;
    return filteredLabelWithDisplayName.filter(
      (row) => new Date(row.createdAt) >= new Date(start) && new Date(end)
    );
  }, [
    labelList,
    selectTimeFrame,
    open,
    selectedLabelCategory,
    hasLabelCategory,
    start,
    end
  ]);

  const { filteredList, handleFilter } = useFiltersByName<ILabel>(filterByDate);
  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<ILabel>(filteredList);

  const headerCheckboxState = useMemo(() => {
    const visibleIds = new Set(infiniteScroll.list.map((item) => item.id));
    // how many visible label selected
    const visibleSelectedCount = selectedLabelsLocal.filter((item) =>
      visibleIds.has(item.id)
    ).length;

    return {
      indeterminate:
        visibleSelectedCount > 0 && visibleSelectedCount < visibleIds.size,
      checked: visibleSelectedCount === visibleIds.size
    };
  }, [infiniteScroll.list, selectedLabelsLocal]);

  const handleClose = (e) => {
    handleFilter(''); // reset filter
    // setSelectedLabelsLocal(prevSelectedLabels);
    onClose(e);
  };

  useEffect(() => {
    if (!open) return;
    setSelectedLabelsLocal(prevSelectedLabels);
  }, [prevSelectedLabels, open]);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'md'}>
      <DialogTitle
        sx={{
          m: theme.spacing(3, 3, 1)
        }}
      >
        <IconButton
          aria-label="close"
          onClick={(e) => handleClose(e)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h3" sx={{ textAlign: 'left' }}>
          {t(title.toLowerCase())}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          m: { md: theme.spacing(1, 3) }
        }}
      >
        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{ width: '100%' }}
          gap={1}
        >
          <ModalNewLabel
            variantButton="contained"
            sxButton={{
              borderRadius: '6px',
              minWidth: theme.spacing(15),
              height: 36,
              background: theme.palette.primary.dark
            }}
            titleButton="Add Label"
            dataList={labelList}
            isHidden={false}
            hasLabelCategory={hasLabelCategory}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="label-category-label">
              {t('label category')}
            </InputLabel>
            <Select
              labelId="label-category-label"
              id="labelCategory"
              name="labelCategory"
              label="Label Category"
              value={selectedLabelCategory ?? 'none'}
              onChange={(e) =>
                setSelectedLabelCategory(
                  e.target.value === 'none'
                    ? null
                    : (e.target.value as TLabelCategory)
                )
              }
            >
              {['all', 'none', 'topic', 'perception'].map((item) => (
                <MenuItem key={item} value={item}>
                  {item === 'all' ? t('all') : capitalize(item)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Stack
          direction="row"
          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
          spacing={2}
          sx={{ mb: theme.spacing(1) }}
        >
          <Box sx={{ minWidth: theme.spacing(25) }}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<FilterNoneIcon />}
                  checkedIcon={<LibraryAddCheckIcon />}
                />
              }
              label={t('show by timeframe')}
              onChange={() => setSelectTimeFrame((click) => !click)}
            />
          </Box>
          <TextField
            fullWidth
            id="standard-basic"
            placeholder={t('search label')}
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{
              mb: theme.spacing(3)
            }}
            onChange={(e) => handleFilter(e.target.value)}
          />
        </Stack>

        <InfiniteScroll
          dataLength={infiniteScroll.list.length}
          next={fetchMoreData}
          hasMore={infiniteScroll.hasMore}
          loader={<div />}
          height={300}
          endMessage={<div />}
        >
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCellHeader padding="checkbox">
                    <Checkbox
                      indeterminate={headerCheckboxState.indeterminate}
                      checked={headerCheckboxState.checked}
                      size="small"
                      sx={{ ml: theme.spacing(0.2) }}
                      icon={
                        <FilterNoneIcon
                          sx={{
                            color: theme.palette.common.white,
                            width: theme.spacing(2.3),
                            height: theme.spacing(2.3)
                          }}
                        />
                      }
                      checkedIcon={
                        <LibraryAddCheckIcon
                          sx={{
                            color: theme.palette.common.white,
                            width: theme.spacing(2.3),
                            height: theme.spacing(2.3)
                          }}
                        />
                      }
                      indeterminateIcon={
                        <IndeterminateCheckBoxIcon
                          sx={{
                            color: theme.palette.common.white,
                            width: theme.spacing(2.2),
                            height: theme.spacing(2.2)
                          }}
                        />
                      }
                      onChange={handleSelectAllClick}
                    />
                  </TableCellHeader>
                  <TableCellHeader align="left" size="small">
                    <Typography
                      variant="subtitle2"
                      sx={{ fontSize: 13, color: theme.palette.common.white }}
                    >
                      {t('label list')}
                    </Typography>
                  </TableCellHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {infiniteScroll.list.length > 0 ? (
                  infiniteScroll.list.map((label) => {
                    const isChecked = isSelected(label.id);
                    return (
                      <TableRow key={label.id}>
                        <TableCell align="center" size="small">
                          <FormControlLabel
                            sx={{ marginLeft: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={isChecked}
                                onChange={(e) => {
                                  handleClickLabel(label);
                                }}
                                name="labels"
                                color="primary"
                                value={label.id}
                              />
                            }
                            label=""
                          />
                        </TableCell>
                        <TableCell align="left" size="small">
                          {label.displayName}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow hover={false}>
                    <TableCell align="center" colSpan={2}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <Avatar>N/A</Avatar>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              handleFilter('');
              onSubmit(selectedLabelsLocal);
            }}
            sx={{ width: theme.spacing(20), mt: theme.spacing(2) }}
          >
            {t('done')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ModalSelectLabel;
